import { useEffect, useRef, useState } from "react";
import {
  DAYS,
  getDaySlot,
  isToday,
  DEFAULT_COL_WIDTH,
  MIN_COL_WIDTH,
  formatHour,
} from "./rosterHelper";
import TimeRangeModal from "./EditTime/TimeRangeModal";
import ResetStaffNormalHours from "./EditTime/ResetStaffNormalHours";

const RosterTable = ({
  staffs,
  dates,
  colWidths,
  setColWidths,
  draftRoster,
  setDraftRoster,
  closedDates,
  originalOverrides,
  onResetStaffSuccess, // ✅ ADD
}) => {
  const [editing, setEditing] = useState(null);

  const scrollRef = useRef(null);

  /* ---------------- helpers ---------------- */

  const toLocalDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const didAutoScroll = useRef(false);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (didAutoScroll.current) return;

    const todayIndex = dates.findIndex(isToday);
    if (todayIndex === -1) return;

    let scrollLeft = 0;
    for (let i = 0; i < todayIndex; i++) {
      scrollLeft += colWidths[i] || DEFAULT_COL_WIDTH;
    }

    scrollRef.current.scrollLeft = scrollLeft;
    didAutoScroll.current = true;
  }, [dates]); // ❗ REMOVE colWidths dependency

  const isModified = (key, draft) => {
    const original = originalOverrides[key];
    if (!original) return true; // new override

    return (
      original.dayOff !== draft.dayOff ||
      original.startHour !== draft.startHour ||
      original.endHour !== draft.endHour
    );
  };

  const getKey = (staffId, date) => `${staffId}__${toLocalDateKey(date)}`;

  const getDraft = (staffId, date, fallback) => {
    const key = getKey(staffId, date);
    const override = draftRoster[key];

    // ✅ If override exists, ALWAYS use it
    if (override) {
      return {
        dayOff: !!override.dayOff,
        startHour: override.startHour,
        endHour: override.endHour,
      };
    }

    // fallback = normal working hours
    return {
      dayOff: false,
      startHour: fallback.startHour,
      endHour: fallback.endHour,
    };
  };

  const updateDraft = (staffId, date, patch) => {
    const key = getKey(staffId, date);
    setDraftRoster((p) => ({
      ...p,
      [key]: { ...p[key], ...patch },
    }));
  };

  /* ---------------- resize ---------------- */

  const startResize = (i, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[i];

    const move = (ev) =>
      setColWidths((p) => ({
        ...p,
        [i]: Math.max(MIN_COL_WIDTH, startW + ev.clientX - startX),
      }));

    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };

    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const normalizeClosedDate = (input) => {
    if (!input) return null;

    // If already YYYY-MM-DD
    if (typeof input === "string" && input.length === 10) {
      return input;
    }

    // ISO string or Date
    const d = new Date(input);
    if (isNaN(d)) return null;

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const isDateClosed = (date) => {
    const dayKey = toLocalDateKey(date);

    return closedDates?.some((c) => {
      const closedKey = normalizeClosedDate(c.date);
      return closedKey === dayKey;
    });
  };

  const isTimeChanged = (key, draft, baseSlot) => {
    const original = originalOverrides[key];

    const ref = original ?? baseSlot;

    return ref.startHour !== draft.startHour || ref.endHour !== draft.endHour;
  };

  return (
    <div
      ref={scrollRef}
      className="overflow-x-auto scrollbar-thin bg-dry border border-border rounded"
    >
      <table className="border-collapse w-max">
        <thead>
          <tr>
            <th className="sticky left-0 z-20 bg-dry p-3 min-w-[110px] border-r border-border text-textGray lg:min-w-[220px]">
              Staff
            </th>
            {dates.map((d, i) => {
              const closed = isDateClosed(d); // ✅ ADD
              return (
                <th
                  key={d}
                  style={{ width: colWidths[i] || DEFAULT_COL_WIDTH }}
                  className={`relative p-3 text-center border-r border-border ${
                    isToday(d) ? "bg-drylightextra" : "bg-dry"
                  }`}
                >
                  {closed && (
                    <span className="absolute -bottom-2 right-4 text-[10px] px-1.5 py-0.5 rounded bg-yellowlight text-yellowdark uppercase">
                      Closed
                    </span>
                  )}
                  <div className="text-sm text-textGray">{d.getDate()}</div>
                  <div className="text-xs text-textGray">
                    {DAYS[d.getDay()].slice(0, 3)}
                  </div>

                  <div
                    onMouseDown={(e) => startResize(i, e)}
                    onDoubleClick={() =>
                      setColWidths((p) => ({
                        ...p,
                        [i]: DEFAULT_COL_WIDTH,
                      }))
                    }
                    className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-border"
                  />
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {staffs.map((staff) => (
            <tr key={staff._id}>
              {/* STAFF NAME */}
              <td className="sticky left-0 bg-dry p-3 border-t border-border text-textGray">
                <div className="font-medium text-sm md:text-md">
                  {staff.name}
                </div>
                <ResetStaffNormalHours
                  staffId={staff._id}
                  staffName={staff.name}
                  onSuccess={onResetStaffSuccess}
                />
              </td>

              {/* STAFF / DATE CELLS */}
              {dates.map((date, i) => {
                const businessClosed = isDateClosed(date);

                /* 🚫 BUSINESS CLOSED = FORCE DAY OFF */
                if (businessClosed) {
                  return (
                    <td
                      key={toLocalDateKey(date)}
                      style={{ width: colWidths[i] }}
                      className="border border-dry bg-drylight text-textGray"
                    >
                      <div className="flex flex-col items-center pt-2 pb-3 h-full">
                        <div className="text-xl font-semibold">✕</div>
                      </div>
                    </td>
                  );
                }

                const baseSlot = getDaySlot(staff, DAYS[date.getDay()]);

                if (!baseSlot) {
                  return (
                    <td
                      key={toLocalDateKey(date)}
                      style={{ width: colWidths[i] }}
                      className="bg-drylightextra text-center text-xs border border-border"
                    >
                      Closed
                    </td>
                  );
                }

                const draft = getDraft(staff._id, date, baseSlot);

                // ✅ CORRECT SCOPE
                const key = getKey(staff._id, date);
                const modified = isModified(key, draft);
                const hasOverride = !!originalOverrides[key];

                const timeChanged = isTimeChanged(key, draft, baseSlot);

                return (
                  <td
                    key={toLocalDateKey(date)}
                    style={{ width: colWidths[i] }}
                    className={`
    border border-border transition-colors
    ${
      businessClosed
        ? "bg-drylight text-textGray"
        : draft.dayOff
        ? modified
          ? "bg-yellowdark text-yellowlight" // 🔵 modified day off
          : hasOverride
          ? "bg-yellow-800 text-white" // 🟡 saved override
          : "bg-dry text-gray-400" // ⚪ normal day off
        : modified
        ? "bg-yellowlight text-yellowdark" // 🔵 modified working
        : hasOverride
        ? "bg-yellowdark text-white" // 🟡 saved override
        : "bg-white text-yellowlight" // ⚪ normal
    }
  `}
                  >
                    {draft.dayOff ? (
                      <button
                        className="w-full h-full text-2xl text-textGray"
                        onClick={() =>
                          updateDraft(staff._id, date, { dayOff: false })
                        }
                      >
                        ✕
                      </button>
                    ) : (
                      <div className="flex flex-col items-center py-2">
                        <div className="relative group">
                          <div
                            className="rounded-full px-3 py-1 hover:bg-black/20 cursor-pointer"
                            onClick={() =>
                              setEditing({
                                staffId: staff._id,
                                date,
                                slot: draft,
                              })
                            }
                          >
                            <div
                              className={`text-sm font-semibold ${
                                timeChanged ? "text-red-500 font-normal" : ""
                              }`}
                            >
                              {formatHour(draft.startHour)}
                            </div>
                            <div
                              className={`text-sm font-semibold ${
                                timeChanged ? "text-red-500 font-normal" : ""
                              }`}
                            >
                              {formatHour(draft.endHour)}
                            </div>
                          </div>

                          {/* Tooltip */}
                          <div
                            className="
    absolute bottom-full left-1/2 -translate-x-1/2 mb-2
    hidden group-hover:block
    whitespace-nowrap
    bg-black text-white text-[10px]
    px-2 py-1 rounded-md
    shadow-lg z-50
  "
                          >
                            Click to edit working hours
                          </div>
                        </div>

                        <div className="relative group inline-block">
                          <button
                            className="
    mt-0
    w-6 h-6
    flex items-center justify-center
    text-xs cursor-pointer
    rounded-full
    hover:bg-black/20
    transition
  "
                            onClick={() =>
                              updateDraft(staff._id, date, { dayOff: true })
                            }
                          >
                            ✓
                          </button>

                          {/* Tooltip */}
                          <div
                            className="
    absolute bottom-full left-1/2 -translate-x-1/2 mb-1
    hidden group-hover:block
    whitespace-nowrap
    bg-black text-white text-[10px]
    px-2 py-1 rounded-md
    shadow-lg z-50
  "
                          >
                            Mark as day off
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <TimeRangeModal
          initialSlot={editing.slot}
          onClose={() => setEditing(null)}
          onSave={(range) => {
            updateDraft(editing.staffId, editing.date, range);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

export default RosterTable;
